import { useEffect, useState, type ComponentType, type ReactNode } from 'react'
import { Menu, X } from 'lucide-react'
import type { PermissionCode } from '@concours/shared'
import { usePermissions } from '../rbac/permissions'
import { Button } from './Button'

export interface NavItem {
  label: string
  href: string
  icon?: ComponentType<{ 'aria-hidden'?: boolean }>
  /**
   * Permissions requises pour voir l'entrée (au moins une). Le menu est
   * entièrement piloté par cette configuration (TODO 4.6).
   */
  anyOf?: readonly PermissionCode[]
  badge?: ReactNode
}

export interface NavSection {
  title?: string
  items: NavItem[]
}

export interface LinkComponentProps {
  href: string
  className?: string
  'aria-current'?: 'page'
  onClick?: () => void
  children: ReactNode
}

/** Identité affichée en tête de la navigation. `logo` : image officielle (jamais recréée en CSS). */
export interface Brand {
  name: string
  sub?: string
  mark?: string
  href?: string
  logo?: string
}

function BrandBlock({ brand }: { brand: Brand }) {
  return (
    <>
      {brand.logo ? (
        <img className="brand-logo" src={brand.logo} alt="" aria-hidden="true" />
      ) : (
        <span className="brand-mark" aria-hidden="true">
          {brand.mark ?? brand.name.charAt(0)}
        </span>
      )}
      <span className="brand-text">
        <span className="brand-name">{brand.name}</span>
        {brand.sub && <span className="brand-sub">{brand.sub}</span>}
      </span>
    </>
  )
}

export interface AppShellProps {
  brand: Brand
  nav: NavSection[]
  currentPath: string
  /** Lien du routeur de l'application (ex. adaptateur React Router). */
  linkComponent?: ComponentType<LinkComponentProps>
  topbarTitle?: ReactNode
  topbarActions?: ReactNode
  sidebarFooter?: ReactNode
  children: ReactNode
}

function DefaultLink({ href, children, ...rest }: LinkComponentProps) {
  return (
    <a href={href} {...rest}>
      {children}
    </a>
  )
}

export function isActivePath(currentPath: string, href: string): boolean {
  if (href === '/') return currentPath === '/'
  return currentPath === href || currentPath.startsWith(`${href}/`)
}

/**
 * Coque du portail admin : sidebar (248px → icônes ≤1024px → tiroir ≤640px),
 * topbar sticky, contenu centré max 1440px. Les entrées de menu sans
 * permission sont masquées.
 */
export function AppShell({
  brand,
  nav,
  currentPath,
  linkComponent: Link = DefaultLink,
  topbarTitle,
  topbarActions,
  sidebarFooter,
  children,
}: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const permissions = usePermissions()

  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setDrawerOpen(false)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [drawerOpen])

  const sections = nav
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.anyOf || permissions.canAny(item.anyOf)),
    }))
    .filter((section) => section.items.length > 0)

  return (
    <div className="app" data-drawer={drawerOpen ? 'open' : 'closed'}>
      <a className="skip-link" href="#contenu">
        Aller au contenu
      </a>

      <aside className="sidebar" id="navigation" aria-label="Navigation principale">
        <div className="flag-band" aria-hidden="true" />
        <Link href={brand.href ?? '/'} className="sidebar-brand">
          <BrandBlock brand={brand} />
        </Link>

        <nav className="sidebar-nav">
          {sections.map((section, index) => (
            <div className="nav-section" key={section.title ?? index}>
              {section.title && <div className="nav-section-title">{section.title}</div>}
              {section.items.map((item) => {
                const active = isActivePath(currentPath, item.href)
                const Icon = item.icon
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="nav-item"
                    aria-current={active ? 'page' : undefined}
                    onClick={() => setDrawerOpen(false)}
                  >
                    {Icon && <Icon aria-hidden />}
                    <span className="nav-label">{item.label}</span>
                    {item.badge && <span className="nav-badge">{item.badge}</span>}
                  </Link>
                )
              })}
            </div>
          ))}
        </nav>

        {sidebarFooter && <div className="sidebar-foot">{sidebarFooter}</div>}
      </aside>

      <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />

      <div className="main">
        <header className="topbar">
          <Button
            className="burger"
            variant="ghost"
            iconOnly
            icon={<Menu />}
            aria-label="Ouvrir le menu"
            aria-controls="navigation"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          />
          <div className="topbar-title">{topbarTitle}</div>
          <div className="topbar-actions">{topbarActions}</div>
        </header>
        <main className="content" id="contenu" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  )
}

export interface PublicShellProps {
  brand: Brand
  linkComponent?: ComponentType<LinkComponentProps>
  /** Liens de navigation principaux (au centre de l'en-tête ; menu déroulant sur petit écran). */
  headerNav?: ReactNode
  headerActions?: ReactNode
  /** Contenu ajouté sous les liens dans le menu mobile (ex. connexion / inscription). */
  mobileMenuFooter?: ReactNode
  /** Contenu pleine largeur (sections en bandes) : pas de marge ni de largeur maximale. */
  fullWidth?: boolean
  footer?: ReactNode
  children: ReactNode
}

/** Coque sans sidebar : portail candidat (pages publiques et espace candidat). */
export function PublicShell({
  brand,
  linkComponent: Link = DefaultLink,
  headerNav,
  headerActions,
  mobileMenuFooter,
  fullWidth = false,
  footer,
  children,
}: PublicShellProps) {
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  return (
    <div className="public-shell">
      <a className="skip-link" href="#contenu">
        Aller au contenu
      </a>
      <div className="public-top" data-menu={menuOpen ? 'open' : 'closed'}>
        <header className="public-header">
          <Link href={brand.href ?? '/'} className="sidebar-brand">
            <BrandBlock brand={brand} />
          </Link>
          {headerNav && (
            <nav className="public-menu" aria-label="Navigation principale">
              {headerNav}
            </nav>
          )}
          <div className="public-nav">{headerActions}</div>
          {headerNav && (
            <Button
              variant="ghost"
              iconOnly
              className="public-burger"
              icon={menuOpen ? <X /> : <Menu />}
              aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={menuOpen}
              aria-controls="menu-mobile"
              onClick={() => setMenuOpen((open) => !open)}
            />
          )}
        </header>
        <div className="flag-band" aria-hidden="true" />
        {headerNav && (
          <div
            id="menu-mobile"
            className="public-mobile-menu"
            hidden={!menuOpen}
            // Un lien suivi referme le menu.
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('a')) setMenuOpen(false)
            }}
          >
            <nav aria-label="Navigation principale (mobile)">{headerNav}</nav>
            {mobileMenuFooter && <div className="public-mobile-menu-foot">{mobileMenuFooter}</div>}
          </div>
        )}
      </div>
      <main className={fullWidth ? 'content-full' : 'content'} id="contenu" tabIndex={-1}>
        {children}
      </main>
      {footer && <footer className="public-footer">{footer}</footer>}
    </div>
  )
}
